import { Button, EllipsisSpan, Table, usePaging, useUpdate } from '@maxtropy/components';
import { FC, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2PvDispersionRateThresholdPagePost,
  V2PvDispersionRateThresholdPagePostResponse,
} from '@maxtropy/dmes-apis-v2';
import {
  PvThresholdType,
  PvThresholdTypeColor,
  PvThresholdTypeLabel,
  PvThresholdTypeList,
  ThresholdDeviceType,
} from './const';
import { TableColumnsType } from 'antd';
import { v4 } from 'uuid';
import styles from './index.module.scss';
import PvModal from './PvModal';

export type DataItem = Exclude<V2PvDispersionRateThresholdPagePostResponse['list'], undefined>[number];

const generateThresholdColumns = (): TableColumnsType<DataItem> => {
  const findItem = (record: DataItem, type: PvThresholdType) =>
    record.thresholdValueResponseList?.find(item => item.type === type);

  return PvThresholdTypeList.map(type => ({
    title: (
      <div className={styles.thresholdTitle} style={{ color: PvThresholdTypeColor[type] }}>
        <div className={styles.thresholdIcon} style={{ background: PvThresholdTypeColor[type] }}></div>
        {PvThresholdTypeLabel[type]}
      </div>
    ),
    children: [
      {
        title: '异常',
        key: 'abnormalValue',
        ellipsis: { showTitle: true },
        render: (_, record) => (
          <EllipsisSpan
            value={findItem(record, type)?.abnormalValue && findItem(record, type)?.abnormalValue + '%'}
          ></EllipsisSpan>
        ),
      },
      {
        title: '较差',
        key: 'differenceValue',
        ellipsis: { showTitle: true },
        render: (_, record) => (
          <EllipsisSpan
            value={findItem(record, type)?.differenceValue && findItem(record, type)?.differenceValue + '%'}
          ></EllipsisSpan>
        ),
      },
      {
        title: '一般',
        key: 'generalValue',
        ellipsis: { showTitle: true },
        render: (_, record) => (
          <EllipsisSpan
            value={findItem(record, type)?.generalValue && findItem(record, type)?.generalValue + '%'}
          ></EllipsisSpan>
        ),
      },
    ],
  }));
};

interface Props {
  objectName: string;
  pagingInfo: ReturnType<typeof usePaging>;
}

const PvTable: FC<Props> = props => {
  const { objectName, pagingInfo } = props;
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<DataItem>();
  const [updateState, updateFn] = useUpdate();

  const { data, loading } = useRequest(
    async () => {
      const res = await apiV2PvDispersionRateThresholdPagePost({
        page: pageOffset,
        size: pageSize,
        objectName: objectName,
        type: ThresholdDeviceType.PV,
      });
      setTotalCount(res.total ?? 0);
      return res.list ?? [];
    },
    {
      refreshDeps: [pageSize, pageOffset, objectName, updateState],
    }
  );

  const columns: TableColumnsType<DataItem> = [
    {
      title: '',
      children: [
        {
          title: '光伏站名称',
          dataIndex: 'pvStationName',
          ellipsis: { showTitle: true },
          width: 176,
          render: v => <EllipsisSpan value={v}></EllipsisSpan>,
        },
        {
          title: '所属运营单元',
          dataIndex: 'ouName',
          ellipsis: { showTitle: true },
          width: 176,
          render: v => <EllipsisSpan value={v}></EllipsisSpan>,
        },
      ],
    },
    ...generateThresholdColumns(),
    {
      title: '操作',
      key: 'action',
      width: 100,
      ellipsis: { showTitle: true },
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            setEditData(record);
            setModalOpen(true);
          }}
        >
          编辑
        </Button>
      ),
    },
  ];

  return (
    <>
      <Table bordered rowKey={() => v4()} loading={loading} dataSource={data} columns={columns}></Table>

      <PvModal open={modalOpen} setOpen={setModalOpen} data={editData} onOk={() => updateFn()}></PvModal>
    </>
  );
};

export default PvTable;
